import { connect } from 'react-redux'
import { List } from 'immutable'
import ImmutablePropTypes from 'react-immutable-proptypes'
import PropTypes from 'prop-types'
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react'
import _get from 'lodash/get'
import { get as getConfig } from '../../../config/index'
import { getEmailCustomerServiceUrl } from '../../../services/url/index'

import Button, { TYPES } from '../../Button/index'
import { H1 } from '../../Heading/index'
import { HEADING_TYPES } from '../../Heading/_Heading'
import { TARGET_BLANK } from '../../Link/constants'
import { CHECKOUT_ORDER_ERROR_TYPE_PAYMENT } from '../../../services/checkout/index'
import Link from '../../Link/index'
import {
  USER_ACCOUNT_PAUSED,
  USER_ACCOUNT_ACTIVE,
  getUserSubscriptionPauseInfo,
} from '../../../services/user-account/index'

function getResumeDescription (status, staticText, resumeDate, price) {
  if (status === USER_ACCOUNT_ACTIVE) {
    return (
      <div className="modal-pause-resume__description">
        {staticText.get('descriptionScheduledPauseResume', '')}
        <span className="modal-pause-resume__date">
          {resumeDate}
        </span>
        {staticText.get('descriptionPauseResumeAt', '')}
        {price}
        <span className="modal-pause-resume__sub">
          {staticText.get('descriptionPauseResumeMo', '')}
        </span>
      </div>
    )
  }

  if (status === USER_ACCOUNT_PAUSED) {
    return (
      <div className="modal-pause-resume__description">
        {staticText.get('descriptionPauseResume', '')}
        <span className="modal-pause-resume__date">
          {resumeDate}
        </span>
        {staticText.get('descriptionPauseResumeAt', '')}
        {price}
        <span className="modal-pause-resume__sub">
          {staticText.get('descriptionPauseResumeMo', '')}
        </span>
      </div>
    )
  }
  return null
}

function getResumeDescription2 (status, staticText, resumeDate, price, resumeProration) {
  if (status === USER_ACCOUNT_ACTIVE) {
    return (
      <div className="modal-pause-resume__description">
        {staticText.get('descriptionScheduledPauseResume', '')}
        <span className="modal-pause-resume__date">
          {resumeDate}.
        </span>
      </div>
    )
  }

  if (status === USER_ACCOUNT_PAUSED) {
    if (resumeProration) {
      return (
        <div className="modal-pause-resume__description">
          {staticText.get('descriptionPauseResume', '')}
          <span className="modal-pause-resume__date">
            {resumeDate}
          </span>
          .
        </div>
      )
    }

    return (
      <div className="modal-pause-resume__description">
        {staticText.get('confirmResume', '')}
        {` ${price} ${staticText.get('today', '')}.`}
      </div>
    )
  }

  return null
}

function renderDescriptionPlaceholder () {
  return (
    <div className="modal-pause-resume__description-placeholder-container">
      <div className="modal-pause-resume__description-placeholder" />
      <div className="modal-pause-resume__description-placeholder" />
      <div className="modal-pause-resume__description-placeholder" />
    </div>
  )
}

function renderResumeDescription ({
  accountPauseAnnualGaiaPlus,
  isPauseInfoInitiallyLoading,
  price,
  resumeDate,
  resumeProration,
  status,
  staticText,
}) {
  if (isPauseInfoInitiallyLoading) {
    return renderDescriptionPlaceholder()
  }

  if (accountPauseAnnualGaiaPlus) {
    return getResumeDescription2(
      status,
      staticText,
      resumeDate,
      price,
      resumeProration,
    )
  }

  return getResumeDescription(status, staticText, resumeDate, price)
}

const ModalPauseResume = ({
  auth,
  price,
  status,
  resumeDate,
  staticText,
  userAccount,
  userLanguage,
  isProcessing,
  resumeAccount,
}) => {
  const [isPauseInfoLoading, setIsPauseInfoLoading] = useState(false)
  const [pauseInfo, setPauseInfo] = useState(null)
  const [resumeViewSnapshot, setResumeViewSnapshot] = useState(null)
  const pauseInfoFetchedRef = useRef(false)
  const accountPauseAnnualGaiaPlus = !!_get(getConfig(), ['features', 'userAccount', 'accountPauseAnnualGaiaPlus'])
  const accountId = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'billingAccountId'])
  const accountNumber = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'billingAccountNumber'])
  const isPauseInfoInitiallyLoading = accountPauseAnnualGaiaPlus && isPauseInfoLoading && !pauseInfo
  const hasError = !userAccount.getIn(['manageSubscription', 'data', 'errors'], List()).isEmpty()
    && !userAccount.getIn(['manageSubscription', 'data', 'success'])
  const paymentError = userAccount.getIn(['manageSubscription', 'data', 'errorCode'], '') === CHECKOUT_ORDER_ERROR_TYPE_PAYMENT

  const onTryAgain = useCallback(() => {
    if (accountPauseAnnualGaiaPlus && pauseInfo) {
      setResumeViewSnapshot({
        status,
        resumeDate,
        price,
        resumeProration: _get(pauseInfo, 'resumeProration', true),
      })
    }
    resumeAccount()
  }, [accountPauseAnnualGaiaPlus, pauseInfo, price, resumeAccount, resumeDate, status])

  useEffect(() => {
    if (!isProcessing) {
      setResumeViewSnapshot(null)
    }
  }, [isProcessing])

  useEffect(() => {
    if (
      !accountPauseAnnualGaiaPlus
      || pauseInfoFetchedRef.current
      || !accountId
      || !accountNumber
    ) {
      return
    }

    pauseInfoFetchedRef.current = true

    async function fetchPauseInfo () {
      setIsPauseInfoLoading(true)

      try {
        const response = await getUserSubscriptionPauseInfo({ auth, accountId, accountNumber })
        setPauseInfo(response)
      } finally {
        setIsPauseInfoLoading(false)
      }
    }

    fetchPauseInfo()
  }, [accountPauseAnnualGaiaPlus, accountId, accountNumber, auth])

  const descriptionStatus = resumeViewSnapshot ? resumeViewSnapshot.status : status
  const descriptionResumeDate = resumeViewSnapshot ? resumeViewSnapshot.resumeDate : resumeDate
  const descriptionPrice = resumeViewSnapshot ? resumeViewSnapshot.price : price
  const descriptionResumeProration = resumeViewSnapshot
    ? resumeViewSnapshot.resumeProration
    : _get(pauseInfo, 'resumeProration', true)

  if (paymentError) {
    return (
      <div className="modal-pause-resume-payment-error">
        <H1 as={[HEADING_TYPES.H3_MOBILE, HEADING_TYPES.H2_DESKTOP]}>{staticText.get('titlePauseResumeUpdatePayment', '')}</H1>
        <div className="modal-pause-resume-error__description">
          <span className="modal-pause-resume-error__important">
            {staticText.get('descriptionPauseResumeUpdatePayment1', '')}
          </span>
          {staticText.get('descriptionPauseResumeUpdatePayment2', '')}
          <Link
            className="modal-pause-resume-error__link"
            to={getEmailCustomerServiceUrl(userLanguage)}
            directLink
            target={TARGET_BLANK}
          >
            {staticText.get('descriptionPauseResumeUpdatePayment3', '')}
          </Link>
          {staticText.get('descriptionPauseResumeUpdatePayment4', '')}
        </div>
        <div className="modal-pause-resume-error__buttons">
          <Button
            className="modal-pause-resume-error__button"
            onClick={onTryAgain}
            disabled={isProcessing}
            type={TYPES.PRIMARY}
          >
            {isProcessing
              ? staticText.get('buttonPauseProcessing', '')
              : staticText.get('buttonPauseResumeUpdatePayment', '')
            }
          </Button>
        </div>
      </div>
    )
  }

  if (hasError) {
    return (
      <div className="modal-pause-resume-error">
        <H1 as={[HEADING_TYPES.H3_MOBILE, HEADING_TYPES.H2_DESKTOP]}>{staticText.get('titlePauseResumeError', '')}</H1>
        <div className="modal-pause-resume-error__description">
          {staticText.get('descriptionPauseResumeError1', '')}
          <Link
            className="modal-pause-resume-error__link"
            to={getEmailCustomerServiceUrl(userLanguage)}
            directLink
            target={TARGET_BLANK}
          >
            {staticText.get('descriptionPauseResumeError2', '')}
          </Link>
          {staticText.get('descriptionPauseResumeError3', '')}
        </div>
        <div className="modal-pause-resume-error__buttons">
          <Button
            className="modal-pause-resume-error__button"
            onClick={onTryAgain}
            disabled={isProcessing}
            type={TYPES.PRIMARY}
          >
            {isProcessing
              ? staticText.get('buttonPauseProcessing', '')
              : staticText.get('buttonPauseResumeErrorRetry', '')
            }
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="modal-pause-resume">
      <H1 as={[HEADING_TYPES.H3_MOBILE, HEADING_TYPES.H2_DESKTOP]}>{staticText.get('titlePauseResume', '')}</H1>
      {renderResumeDescription({
        accountPauseAnnualGaiaPlus,
        isPauseInfoInitiallyLoading,
        price: descriptionPrice,
        resumeDate: descriptionResumeDate,
        resumeProration: descriptionResumeProration,
        status: descriptionStatus,
        staticText,
      })}
      <div className="modal-pause-resume__buttons">
        <Button
          className="modal-pause-resume__button"
          onClick={onTryAgain}
          disabled={isProcessing || isPauseInfoInitiallyLoading}
          type={TYPES.PRIMARY}
        >
          {isProcessing ? staticText.get('buttonPauseProcessing', '') : staticText.get('buttonPauseResume', '')}
        </Button>
      </div>
    </div>
  )
}

ModalPauseResume.propTypes = {
  auth: ImmutablePropTypes.map.isRequired,
  user: ImmutablePropTypes.map.isRequired,
  userAccount: ImmutablePropTypes.map.isRequired,
  staticText: ImmutablePropTypes.map.isRequired,
  status: PropTypes.string.isRequired,
  isProcessing: PropTypes.bool.isRequired,
  resumeAccount: PropTypes.func.isRequired,
}

export default connect((state) => ({
  staticText: state.staticText.getIn(['data', 'modalPauseResume', 'data']),
  isProcessing: state.userAccount.getIn(['manageSubscription', 'data', 'accountResumeProcessing'], false)
  || state.userAccount.getIn(['manageSubscription', 'data', 'success'], false),
  auth: state.auth,
  user: state.user,
  userAccount: state.userAccount,
}))(ModalPauseResume)
